import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getProviderTrainings, createTraining, updateTraining,
  uploadOmslagfoto, uploadBrochure, togglePublishTraining, getAcademySubcategorieen
} from '../services/opleidingenApi';
import { Training } from '../types';
import { ArrowLeft, Upload, Image, FileText, Save, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { OpleiderAcademySessies } from '../components/OpleiderAcademySessies';

const SOORT_OPTIES = ['Workshop', 'Masterclass', 'Meerdaagse cursus', 'E-learning'];
const NIVEAU_OPTIES = ['Junior', 'Medior', 'Senior', 'HBO', 'WO'];

type FormData = Partial<Omit<Training, 'id' | 'provider_id' | 'created_at' | 'updated_at' | 'academy_categorie' | 'academy_subcategory_id' | 'academy_tags' | 'pro_hoofdmap' | 'pe_punten' | 'pe_register' | 'b2b_rij' | 'b2b_vanaf_prijs' | 'b2b_min_groep' | 'b2b_max_groep'>> & {
  /** Belagom Academy hoofdgroep; '' = niet in de Academy → null naar de API. */
  academy_categorie?: string;
  /** Subcategorie-id als string (select); '' = geen. */
  academy_subcategory_id?: string;
  /** Trefwoorden, komma-gescheiden invoer; backend normaliseert. */
  academy_tags?: string;
  /** Spec is_wkr_belast: aan = hobby/privé (WKR-vrije ruimte), uit = beroepsgroei (gerichte vrijstelling). */
  is_wkr_belast?: boolean;
  /** Professional Academy: hoofdmap (rij 1-6), PE-punten als tekst-invoer, register, examen. */
  pro_hoofdmap?: string;
  pe_punten?: string;
  pe_register?: string;
  inclusief_examen?: boolean;
  /** B2B Netflix Academy (opdrachtgevers): rij 1-4, inkoop-badge, vanaf-prijs als tekst, ROI-doel, logistiek. */
  b2b_rij?: string;
  b2b_vanaf_prijs?: string;
  /** Discovery Engine: staffel-groepsgrootte als tekst-invoer ('' = geen grens). */
  b2b_min_groep?: string;
  b2b_max_groep?: string;
};

/** Logistieke Locatiematrix (zelfde keys als de RFQ-locatievoorkeur). */
const B2B_LEVERLOCATIES: Array<{ value: 'eigen_locatie' | 'extern' | 'online'; label: string }> = [
  { value: 'eigen_locatie', label: '🏢 On-Site — de trainer reist naar de opdrachtgever' },
  { value: 'extern', label: '🏫 Externe locatie — u verzorgt de trainingsruimte' },
  { value: 'online', label: '💻 Live-Online Classroom (Teams/Zoom)' },
];

/** De 4 strategische rijen van de incompany-catalogus (zelfde keys als src/services/b2bAcademyService.ts). */
const B2B_RIJEN: Array<{ value: string; label: string; fiscaal: string }> = [
  { value: 'mentaal', label: '🧠 Rij 1 — Burn-out Preventie & Mentale Kracht (team)', fiscaal: '100% gericht vrijgesteld (Arbo-preventie)' },
  { value: 'fysiek', label: '🛠️ Rij 2 — Ergonomie & Veiligheid (Arbo, ploegendienst)', fiscaal: '100% gericht vrijgesteld (Arbobesluit)' },
  { value: 'vakinhoudelijk', label: '📈 Rij 3 — Vaktechnische Groei, AI & Leiderschap', fiscaal: '100% belastingvrij (Studie & Opleiding)' },
  { value: 'passie', label: '🤝 Rij 4 — Cultuur, Teambuilding & Intervisie', fiscaal: '⚠️ drukt op het WKR-budget van de werkgever' },
];

/** De 6 hoofdmappen van het expertportaal (zelfde keys als src/constants/proAcademy.ts). */
const PRO_HOOFDMAPPEN: Array<{ value: string; label: string; pe: boolean }> = [
  { value: 'welzijn', label: '🌿 Rij 1 — Welzijn & Vitaliteit', pe: true },
  { value: 'trauma', label: '🚨 Rij 2 — Trauma & Crisis', pe: true },
  { value: 'psa', label: '🧠 Rij 3 — Psychosociale Arbeidsbelasting (PSA)', pe: true },
  { value: 'arbo', label: '🩺 Rij 4 — Arbo & Verzuimregie', pe: true },
  { value: 'ld', label: '📈 Rij 5 — Ontwikkeling & Groei', pe: true },
  { value: 'passie', label: "🎨 Rij 6 — Persoonlijke Passie & Hobby's (geen PE-punten)", pe: false },
];

const SOORT_EMPLOYER_CATEGORIEEN = [
  { value: 'leidinggevende', label: 'Leidinggevenden' },
  { value: 'specialisten', label: 'Specialisten' },
  { value: 'operationeel', label: 'Operationele medewerkers' },
  { value: 'hr_support', label: 'HR en support' },
  { value: 'anders', label: 'Anders' },
];

const LEGE_FORM: FormData = {
  titel: '',
  soort_opleiding: '',
  voor_wie: '',
  wanneer: '',
  locaties: '',
  prijs_excl_btw: undefined,
  aanbieder_bio: '',
  niveau: '',
  duur: '',
  samenvatting: '',
  leerdoelen: '',
  doelgroep: '',
  programma_onderdelen: '',
  datum_tijd: '',
  locatie_detail: '',
  locatie_postcode: '',
  inclusief_materialen: false,
  lunch_inbegrepen: false,
  studiemateriaal_info: '',
  studiebelasting: '',
  accreditatie: '',
  certificering: '',
  docenten: '',
  toelatingseisen: '',
  video_url: '',
  target_audience: 'professional',
  employer_category: '',
  advertentie_type: 'geen',
  academy_categorie: '',
  academy_subcategory_id: '',
  academy_tags: '',
  is_wkr_belast: false,
  academy_pitch: '',
  pro_hoofdmap: '',
  pe_punten: '',
  pe_register: '',
  inclusief_examen: false,
  b2b_rij: '',
  b2b_inkoop_type: 'incompany',
  b2b_vanaf_prijs: '',
  b2b_organisatie_doel: '',
  b2b_duur_logistiek: '',
  b2b_leverlocaties: ['eigen_locatie', 'extern'],
  b2b_min_groep: '',
  b2b_max_groep: '',
};

export const OpleiderTrainingFormPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormData>(LEGE_FORM);
  const [isPublished, setIsPublished] = useState(false);
  const [omslagFile, setOmslagFile] = useState<File | null>(null);
  const [omslagPreview, setOmslagPreview] = useState<string | null>(null);
  const [brochureFile, setBrochureFile] = useState<File | null>(null);
  const [openSection, setOpenSection] = useState<string>('basis');

  // In edit mode, load existing training
  const { data: trainings } = useQuery({
    queryKey: ['provider-trainings'],
    queryFn: getProviderTrainings,
    enabled: isEdit,
  });

  useEffect(() => {
    if (isEdit && trainings) {
      const t = trainings.find((tr) => tr.id === parseInt(id!));
      if (t) {
        if (t.omslagfoto_url) setOmslagPreview(t.omslagfoto_url);
        setIsPublished(t.is_published || false);
        setForm({
          titel: t.titel || '',
          soort_opleiding: t.soort_opleiding || '',
          voor_wie: t.voor_wie || '',
          wanneer: t.wanneer || '',
          locaties: t.locaties || '',
          prijs_excl_btw: t.prijs_excl_btw,
          aanbieder_bio: t.aanbieder_bio || '',
          niveau: t.niveau || '',
          duur: t.duur || '',
          samenvatting: t.samenvatting || '',
          leerdoelen: t.leerdoelen || '',
          doelgroep: t.doelgroep || '',
          programma_onderdelen: t.programma_onderdelen || '',
          datum_tijd: t.datum_tijd || '',
          locatie_detail: t.locatie_detail || '',
          locatie_postcode: t.locatie_postcode || '',
          inclusief_materialen: t.inclusief_materialen || false,
          lunch_inbegrepen: t.lunch_inbegrepen || false,
          studiemateriaal_info: t.studiemateriaal_info || '',
          studiebelasting: t.studiebelasting || '',
          accreditatie: t.accreditatie || '',
          certificering: t.certificering || '',
          docenten: t.docenten || '',
          toelatingseisen: t.toelatingseisen || '',
          video_url: t.video_url || '',
          target_audience: t.target_audience || 'professional',
          employer_category: t.employer_category || '',
          advertentie_type: t.advertentie_type || 'geen',
          academy_categorie: t.academy_categorie || '',
          academy_subcategory_id: t.academy_subcategory_id ? String(t.academy_subcategory_id) : '',
          academy_tags: (t.academy_tags || []).join(', '),
          is_wkr_belast: t.wkr_label === 'wkr_vrije_ruimte',
          academy_pitch: t.academy_pitch || '',
          pro_hoofdmap: t.pro_hoofdmap || '',
          pe_punten: t.pe_punten !== null && t.pe_punten !== undefined ? String(t.pe_punten) : '',
          pe_register: t.pe_register || '',
          inclusief_examen: Boolean(t.inclusief_examen),
          b2b_rij: t.b2b_rij || '',
          b2b_inkoop_type: t.b2b_inkoop_type || 'incompany',
          b2b_vanaf_prijs: t.b2b_vanaf_prijs !== null && t.b2b_vanaf_prijs !== undefined ? String(t.b2b_vanaf_prijs) : '',
          b2b_organisatie_doel: t.b2b_organisatie_doel || '',
          b2b_duur_logistiek: t.b2b_duur_logistiek || '',
          b2b_leverlocaties: Array.isArray(t.b2b_leverlocaties) && t.b2b_leverlocaties.length ? t.b2b_leverlocaties : ['eigen_locatie', 'extern'],
          b2b_min_groep: t.b2b_min_groep !== null && t.b2b_min_groep !== undefined ? String(t.b2b_min_groep) : '',
          b2b_max_groep: t.b2b_max_groep !== null && t.b2b_max_groep !== undefined ? String(t.b2b_max_groep) : '',
        });
      }
    }
  }, [isEdit, trainings, id]);

  const toggleMutation = useMutation({
    mutationFn: () => togglePublishTraining(parseInt(id!)),
    onSuccess: (data) => {
      setIsPublished(data.is_published || false);
      queryClient.invalidateQueries({ queryKey: ['provider-trainings'] });
    },
  });

  const academyCatalogus = useQuery({ queryKey: ['academy-subcategorieen'], queryFn: getAcademySubcategorieen, staleTime: 5 * 60_000 });
  const hoofdgroepen = academyCatalogus.data?.hoofdgroepen ?? [];
  const subcategorieen = (academyCatalogus.data?.subcategorieen ?? []).filter((s) => s.hoofdgroep === form.academy_categorie);

  /** Hoofdgroep-wissel reset de subcategorie en zet het WKR-standaardlabel van de groep. */
  const setHoofdgroep = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const key = e.target.value;
    const info = hoofdgroepen.find((h) => h.key === key);
    setForm((p) => ({
      ...p,
      academy_categorie: key,
      academy_subcategory_id: '',
      is_wkr_belast: info ? info.wkr_label === 'wkr_vrije_ruimte' : false,
    }));
  };

  /** API-payload: lege Academy-hoofdgroep gaat als null mee; tags als array; subcategorie als number. */
  const buildPayload = (): Partial<Training> => {
    const { academy_categorie, academy_subcategory_id, academy_tags, is_wkr_belast, pro_hoofdmap, pe_punten, pe_register, inclusief_examen, b2b_rij, b2b_vanaf_prijs, b2b_min_groep, b2b_max_groep, ...rest } = form;
    const geheel = (v: string | undefined) => ((v ?? '').trim() ? parseInt((v ?? '').trim(), 10) : null);
    const inAcademy = Boolean(academy_categorie);
    const payload = {
      ...rest,
      academy_categorie: inAcademy ? academy_categorie : null,
      academy_subcategory_id: inAcademy && academy_subcategory_id ? parseInt(academy_subcategory_id, 10) : null,
      academy_tags: inAcademy ? (academy_tags || '').split(',').map((x) => x.trim()).filter(Boolean) : [],
      is_wkr_belast: inAcademy ? Boolean(is_wkr_belast) : false,
      // Professional Academy: hoofdmap verplicht voor professional/both; passie = nooit PE-punten
      pro_hoofdmap: pro_hoofdmap || null,
      pe_punten: pro_hoofdmap !== 'passie' && (pe_punten ?? '').trim() ? parseFloat((pe_punten ?? '').replace(',', '.')) : null,
      pe_register: pro_hoofdmap !== 'passie' ? (pe_register ?? '').trim() || null : null,
      inclusief_examen: Boolean(inclusief_examen),
      // B2B Netflix Academy: rij verplicht voor employer/both; vanaf-prijs per groep (excl. btw)
      b2b_rij: b2b_rij || null,
      b2b_vanaf_prijs: (b2b_vanaf_prijs ?? '').trim() ? parseFloat((b2b_vanaf_prijs ?? '').replace(',', '.')) : null,
      // Discovery Engine: staffel-groepsgrootte (leeg = geen grens)
      b2b_min_groep: geheel(b2b_min_groep),
      b2b_max_groep: geheel(b2b_max_groep),
    };
    return payload as Partial<Training>;
  };

  const createMutation = useMutation({
    mutationFn: () => createTraining(buildPayload()),
    onSuccess: async (data) => {
      await uploadFiles(data.id);
      queryClient.invalidateQueries({ queryKey: ['provider-trainings'] });
      navigate('/opleider/academy/trainingen');
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => updateTraining(parseInt(id!), buildPayload()),
    onSuccess: async () => {
      await uploadFiles(parseInt(id!));
      queryClient.invalidateQueries({ queryKey: ['provider-trainings'] });
      navigate('/opleider/academy/trainingen');
    },
  });

  const uploadFiles = async (trainingId: number) => {
    if (omslagFile) {
      await uploadOmslagfoto(trainingId, omslagFile);
    }
    if (brochureFile) {
      await uploadBrochure(trainingId, brochureFile);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit) updateMutation.mutate();
    else createMutation.mutate();
  };

  const set = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const setCheck = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, [field]: e.target.checked }));

  const handleOmslagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setOmslagFile(file);
      setOmslagPreview(URL.createObjectURL(file));
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  const toggleSection = (s: string) => setOpenSection(openSection === s ? '' : s);

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/opleider/academy/trainingen" className="p-2 rounded-lg hover:bg-gray-200 transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {isEdit ? t('opleider.editTraining') : t('opleider.newTraining')}
              </h1>
              <p className="text-sm text-gray-500">{t('opleider.fillAndSave')}</p>
            </div>
          </div>
          {isEdit && (
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {isPublished ? t('opleider.published') : t('opleider.concept')}
              </span>
              {isPublished ? (
                <button
                  type="button"
                  onClick={() => toggleMutation.mutate()}
                  disabled={toggleMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
                >
                  <EyeOff className="w-4 h-4" />
                  {t('opleider.makeConcept')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => toggleMutation.mutate()}
                  disabled={toggleMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-60"
                >
                  <Eye className="w-4 h-4" />
                  {t('opleider.publish')}
                </button>
              )}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Omslagfoto bovenaan */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <label className="block text-sm font-medium text-gray-700 mb-3">{t('opleider.coverPhoto')}</label>
            <div className="flex items-start gap-4">
              {omslagPreview ? (
                <img src={omslagPreview} alt="Omslagfoto" className="h-28 w-48 rounded-lg object-cover border border-gray-200 flex-shrink-0" />
              ) : (
                <div className="h-28 w-48 rounded-lg bg-gray-100 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 flex-shrink-0">
                  <Image className="w-8 h-8 text-gray-300 mb-1" />
                  <span className="text-xs text-gray-400">{t('opleider.noPhoto')}</span>
                </div>
              )}
              <div className="mt-1">
                <label className="cursor-pointer flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                  <Upload className="w-4 h-4" />
                  {omslagPreview ? t('opleider.changePhoto') : t('opleider.uploadPhoto')}
                  <input type="file" accept="image/*" onChange={handleOmslagChange} className="hidden" />
                </label>
                <p className="text-xs text-gray-400 mt-2">{t('opleider.photoHint')}</p>
              </div>
            </div>
          </div>

          {/* SECTIE: Basisgegevens (overzichtskaart) */}
          <Accordion title={t('opleider.sectionBasic')} open={openSection === 'basis'} onToggle={() => toggleSection('basis')}>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>{t('opleider.trainingTitle')}</label>
                <input type="text" required value={form.titel} onChange={set('titel')} className={inputClass} placeholder={t('opleider.trainingTitlePh')} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t('opleider.trainingType')}</label>
                  <select value={form.soort_opleiding} onChange={set('soort_opleiding')} className={inputClass}>
                    <option value="">{t('opleider.chooseType')}</option>
                    {SOORT_OPTIES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t('opleider.level')}</label>
                  <select value={form.niveau} onChange={set('niveau')} className={inputClass}>
                    <option value="">{t('opleider.chooseLevel')}</option>
                    {NIVEAU_OPTIES.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>{t('opleider.targetShort')}</label>
                <input type="text" value={form.voor_wie} onChange={set('voor_wie')} className={inputClass} placeholder={t('opleider.targetShortPh')} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t('opleider.when')}</label>
                  <input type="text" value={form.wanneer} onChange={set('wanneer')} className={inputClass} placeholder={t('opleider.whenPh')} />
                </div>
                <div>
                  <label className={labelClass}>{t('opleider.locationShort')}</label>
                  <input type="text" value={form.locaties} onChange={set('locaties')} className={inputClass} placeholder={t('opleider.locationShortPh')} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t('opleider.priceExVat')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.prijs_excl_btw ?? ''}
                    onChange={(e) => setForm((p) => ({ ...p, prijs_excl_btw: e.target.value ? parseFloat(e.target.value) : undefined }))}
                    className={inputClass}
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">{t('opleider.priceHint')}</p>
                </div>
                <div>
                  <label className={labelClass}>{t('opleider.duration')}</label>
                  <input type="text" value={form.duur} onChange={set('duur')} className={inputClass} placeholder={t('opleider.durationPh')} />
                </div>
              </div>
            </div>
          </Accordion>

          {/* SECTIE: Doelgroep & Advertentie */}
          <Accordion title={t('opleider.sectionAudience')} open={openSection === 'doelgroep'} onToggle={() => toggleSection('doelgroep')}>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>{t('opleider.audienceLabel')}</label>
                <select value={form.target_audience} onChange={set('target_audience')} className={inputClass}>
                  <option value="professional">{t('opleider.audienceProfessional')}</option>
                  <option value="employer">{t('opleider.audienceEmployer')}</option>
                  <option value="both">{t('opleider.audienceBoth')}</option>
                </select>
              </div>

              {(form.target_audience === 'employer' || form.target_audience === 'both') && (
                <div>
                  <label className={labelClass}>{t('opleider.employerCategory')}</label>
                  <select value={form.employer_category} onChange={set('employer_category')} className={inputClass}>
                    <option value="">{t('opleider.chooseCategory')}</option>
                    {SOORT_EMPLOYER_CATEGORIEEN.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {(form.target_audience === 'employer' || form.target_audience === 'both') && (
                <div className="rounded-lg border border-emerald-600/20 bg-emerald-50/40 p-4 space-y-3">
                  <div>
                    <label className={labelClass}>
                      {t('opleider.b2bRij', { defaultValue: 'B2B Academy — strategische rij (incompany-catalogus voor opdrachtgevers)' })} <span className="text-red-600">*</span>
                    </label>
                    <select value={form.b2b_rij ?? ''} onChange={set('b2b_rij')} className={inputClass} required>
                      <option value="">{t('opleider.b2bRijKies', { defaultValue: 'Kies een rij…' })}</option>
                      {B2B_RIJEN.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    {form.b2b_rij && (
                      <p className="text-xs text-gray-600 mt-1">
                        {t('opleider.b2bFiscaal', { defaultValue: 'Fiscale status voor de werkgever:' })} <strong>{B2B_RIJEN.find((r) => r.value === form.b2b_rij)?.fiscaal}</strong>
                      </p>
                    )}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className={labelClass}>{t('opleider.b2bInkoop', { defaultValue: 'Inkoop-badge' })}</label>
                      <select value={form.b2b_inkoop_type ?? 'incompany'} onChange={set('b2b_inkoop_type')} className={inputClass}>
                        <option value="incompany">🏢 {t('opleider.b2bIncompany', { defaultValue: 'Incompany Maatwerk' })}</option>
                        <option value="bulk_seats">🎟️ {t('opleider.b2bBulk', { defaultValue: 'Bulk Seats' })}</option>
                        <option value="licentie">🔑 {t('opleider.b2bLicentie', { defaultValue: 'Licenties' })}</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>{t('opleider.b2bVanafPrijs', { defaultValue: 'Vanaf-prijs per groep (excl. btw)' })}</label>
                      <input type="number" min={0} step="0.01" value={form.b2b_vanaf_prijs ?? ''} onChange={set('b2b_vanaf_prijs')} className={inputClass} placeholder={t('opleider.b2bVanafPrijsPh', { defaultValue: 'bijv. 950' })} />
                      <p className="text-xs text-gray-500 mt-1">{t('opleider.b2bVanafPrijsHint', { defaultValue: 'Tegel toont "Vanaf € … per groep"; werkgevers zien automatisch 5% BeLagom B2B Voordeel.' })}</p>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>{t('opleider.b2bDoel', { defaultValue: 'Het organisatie-doel (ROI) — wat bereikt de werkgever hiermee?' })}</label>
                    <textarea rows={3} maxLength={2000} value={form.b2b_organisatie_doel ?? ''} onChange={set('b2b_organisatie_doel')} className={inputClass} placeholder={t('opleider.b2bDoelPh', { defaultValue: 'bijv. Het minimaliseren van concentratieverlies tijdens de nachtdienst, resulterend in minder productiefouten' })} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('opleider.b2bLogistiek', { defaultValue: 'Duur & vorm (logistieke blauwdruk)' })}</label>
                    <input type="text" maxLength={255} value={form.b2b_duur_logistiek ?? ''} onChange={set('b2b_duur_logistiek')} className={inputClass} placeholder={t('opleider.b2bLogistiekPh', { defaultValue: 'bijv. 1 dagdeel (4 uur) op locatie bij de werkgever, inclusief digitaal naslagwerk' })} />
                  </div>
                  <fieldset>
                    <legend className={labelClass}>{t('opleider.b2bLeverlocaties', { defaultValue: 'Logistieke locatiematrix — waar levert u dit traject?' })}</legend>
                    <div className="mt-1 grid gap-2 sm:grid-cols-3">
                      {B2B_LEVERLOCATIES.map((o) => {
                        const gekozen = (form.b2b_leverlocaties ?? []).includes(o.value);
                        return (
                          <label key={o.value} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${gekozen ? 'border-[#0B1F3A] bg-blue-50 text-gray-900' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                            <input
                              type="checkbox"
                              checked={gekozen}
                              onChange={() => {
                                const huidig = form.b2b_leverlocaties ?? [];
                                setForm((f) => ({ ...f, b2b_leverlocaties: gekozen ? huidig.filter((x) => x !== o.value) : [...huidig, o.value] }));
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-[#0B1F3A]"
                            />
                            {o.label}
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{t('opleider.b2bLeverlocatiesHint', { defaultValue: 'Werkgevers filteren hierop in de catalogus; reiskosten calculeert u in de offerte.' })}</p>
                  </fieldset>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className={labelClass}>{t('opleider.b2bMinGroep', { defaultValue: 'Minimale groepsgrootte (staffel)' })}</label>
                      <input type="number" min={1} max={10000} step={1} value={form.b2b_min_groep ?? ''} onChange={set('b2b_min_groep')} className={inputClass} placeholder={t('opleider.b2bGroepPh', { defaultValue: 'leeg = geen grens' })} />
                    </div>
                    <div>
                      <label className={labelClass}>{t('opleider.b2bMaxGroep', { defaultValue: 'Maximale groepsgrootte (staffel)' })}</label>
                      <input type="number" min={1} max={10000} step={1} value={form.b2b_max_groep ?? ''} onChange={set('b2b_max_groep')} className={inputClass} placeholder={t('opleider.b2bGroepPh', { defaultValue: 'leeg = geen grens' })} />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('opleider.b2bHint', { defaultValue: 'De one-liner (hover), doelgroep (Voor wie) en het programma komen uit de velden hierboven en verder in dit formulier.' })}
                  </p>
                </div>
              )}

              {(form.target_audience === 'professional' || form.target_audience === 'both') && (
                <div className="rounded-lg border border-[#0B1F3A]/20 bg-blue-50/40 p-4 space-y-3">
                  <div>
                    <label className={labelClass}>
                      {t('opleider.proHoofdmap', { defaultValue: 'Professional Academy — hoofdmap (rij in het expertportaal)' })} <span className="text-red-600">*</span>
                    </label>
                    <select value={form.pro_hoofdmap ?? ''} onChange={set('pro_hoofdmap')} className={inputClass} required>
                      <option value="">{t('opleider.proHoofdmapKies', { defaultValue: 'Kies een hoofdmap…' })}</option>
                      {PRO_HOOFDMAPPEN.map((h) => (
                        <option key={h.value} value={h.value}>{h.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('opleider.proHoofdmapHint', {
                        defaultValue: 'Professionals zien de rij van hun eigen discipline bovenaan. Rij 6 is open voor alle specialismen, puur hobbymatig en levert geen PE-punten op.',
                      })}
                    </p>
                  </div>

                  {form.pro_hoofdmap && form.pro_hoofdmap !== 'passie' && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className={labelClass}>{t('opleider.pePunten', { defaultValue: 'PE-accreditatiepunten' })}</label>
                        <input
                          type="number"
                          min={0}
                          max={999}
                          step={0.5}
                          value={form.pe_punten ?? ''}
                          onChange={set('pe_punten')}
                          className={inputClass}
                          placeholder={t('opleider.pePuntenPh', { defaultValue: 'bijv. 6' })}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>{t('opleider.peRegister', { defaultValue: 'Register (accreditatie)' })}</label>
                        <input
                          type="text"
                          maxLength={80}
                          value={form.pe_register ?? ''}
                          onChange={set('pe_register')}
                          className={inputClass}
                          placeholder={t('opleider.peRegisterPh', { defaultValue: 'bijv. NVAB, RSC / Register Casemanager, NOBCO, NIP' })}
                        />
                      </div>
                      <p className="text-xs text-gray-500 sm:col-span-2">
                        {t('opleider.peHint', { defaultValue: 'Verschijnt als witte accreditatie-badge linksboven op de cursustegel (🎓 6 PE-punten NVAB).' })}
                      </p>
                    </div>
                  )}

                  <label className="flex items-start gap-2 text-sm text-gray-800">
                    <input type="checkbox" checked={Boolean(form.inclusief_examen)} onChange={setCheck('inclusief_examen')} className="mt-0.5 rounded" />
                    <span>
                      <span className="font-medium">{t('opleider.inclusiefExamen', { defaultValue: 'Inclusief examen' })}</span>
                      <span className="block text-xs text-gray-500">{t('opleider.inclusiefExamenHint', { defaultValue: 'Toont de badge 📝 Inclusief examen op de tegel.' })}</span>
                    </span>
                  </label>
                </div>
              )}

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                <div>
                  <label className={labelClass}>
                    {t('opleider.academyCategorie', { defaultValue: 'BeLagom Academy — hoofdgroep (medewerkersportaal)' })}
                  </label>
                  <select value={form.academy_categorie ?? ''} onChange={setHoofdgroep} className={inputClass}>
                    <option value="">{t('opleider.academyNiet', { defaultValue: 'Niet in de Academy' })}</option>
                    {hoofdgroepen.map((h) => (
                      <option key={h.key} value={h.key}>{h.emoji} {h.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('opleider.academyCategorieHint', {
                      defaultValue: 'Medewerkers zoeken via hoofdgroep, subcategorie en trefwoorden. Ze krijgen automatisch 5% inkoopkorting.',
                    })}
                  </p>
                </div>

                {form.academy_categorie && (
                  <>
                    <div>
                      <label className={labelClass}>{t('opleider.academySubcategorie', { defaultValue: 'Subcategorie (thema-pill)' })}</label>
                      <select value={form.academy_subcategory_id ?? ''} onChange={set('academy_subcategory_id')} className={inputClass}>
                        <option value="">{t('opleider.academySubcategorieKies', { defaultValue: 'Kies een subcategorie…' })}</option>
                        {subcategorieen.map((sc) => (
                          <option key={sc.id} value={String(sc.id)}>{sc.emoji ? `${sc.emoji} ` : ''}{sc.naam}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>{t('opleider.academyTags', { defaultValue: 'Trefwoorden (komma-gescheiden)' })}</label>
                      <input
                        type="text"
                        value={form.academy_tags ?? ''}
                        onChange={set('academy_tags')}
                        placeholder={t('opleider.academyTagsPlaceholder', { defaultValue: 'bijv. fotografie, hobby, creatief, lightroom' })}
                        className={inputClass}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t('opleider.academyTagsHint', { defaultValue: 'Woorden waarop medewerkers zoeken in de zoekbalk. Maximaal 20; automatisch in kleine letters.' })}
                      </p>
                    </div>

                    <div>
                      <label className={labelClass}>{t('opleider.academyPitch', { defaultValue: 'One-liner (hover op de cursustegel)' })}</label>
                      <input
                        type="text"
                        maxLength={200}
                        value={form.academy_pitch ?? ''}
                        onChange={set('academy_pitch')}
                        placeholder={t('opleider.academyPitchPlaceholder', { defaultValue: 'bijv. Bespaar 2 uur per dag door repetitief typewerk slim uit te besteden aan AI' })}
                        className={inputClass}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t('opleider.academyPitchHint', { defaultValue: 'Eén korte, prikkelende zin over het directe resultaat. Maximaal 200 tekens.' })}
                      </p>
                    </div>

                    <label className="flex items-start gap-2 text-sm text-gray-800">
                      <input type="checkbox" checked={Boolean(form.is_wkr_belast)} onChange={setCheck('is_wkr_belast')} className="mt-0.5 rounded" />
                      <span>
                        <span className="font-medium">{t('opleider.academyWkrBelast', { defaultValue: 'WKR-belast (hobby / privé)' })}</span>
                        <span className="block text-xs text-gray-500">
                          {t('opleider.academyWkrBelastHint', {
                            defaultValue:
                              'Aan = drukt op de WKR-vrije ruimte van de werkgever (pure hobby, lifestyle). Uit = beroepsgerichte groei, gerichte vrijstelling (belastingvrij).',
                          })}
                        </span>
                      </span>
                    </label>
                  </>
                )}
              </div>

              <div>
                <label className={labelClass}>{t('opleider.adLabel')}</label>
                <select value={form.advertentie_type} onChange={set('advertentie_type')} className={inputClass}>
                  <option value="geen">{t('opleider.adNone')}</option>
                  <option value="professioneel">{t('opleider.adProfessional')}</option>
                  <option value="onderneming">{t('opleider.adEmployer')}</option>
                </select>
                <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                  <p className="font-medium mb-1">{t('opleider.discountTitle')}</p>
                  <p>• <strong>{t('opleider.discountNoAd')}</strong> {t('opleider.discountNoAdText')}</p>
                  <p>• <strong>{t('opleider.discountWithAd')}</strong> {t('opleider.discountWithAdText')}</p>
                </div>
              </div>
            </div>
          </Accordion>

          {/* SECTIE: Inhoud & Resultaat */}
          <Accordion title={t('opleider.sectionContent')} open={openSection === 'inhoud'} onToggle={() => toggleSection('inhoud')}>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>{t('opleider.summary')}</label>
                <textarea value={form.samenvatting} onChange={set('samenvatting')} rows={4} className={`${inputClass} resize-none`} placeholder={t('opleider.summaryPh')} />
              </div>
              <div>
                <label className={labelClass}>{t('opleider.learningGoals')}</label>
                <textarea value={form.leerdoelen} onChange={set('leerdoelen')} rows={4} className={`${inputClass} resize-none`} placeholder={t('opleider.learningGoalsPh')} />
              </div>
              <div>
                <label className={labelClass}>{t('opleider.targetSpecific')}</label>
                <input type="text" value={form.doelgroep} onChange={set('doelgroep')} className={inputClass} placeholder={t('opleider.targetSpecificPh')} />
              </div>
              <div>
                <label className={labelClass}>{t('opleider.programParts')}</label>
                <textarea value={form.programma_onderdelen} onChange={set('programma_onderdelen')} rows={4} className={`${inputClass} resize-none`} placeholder={t('opleider.programPartsPh')} />
              </div>
              <div>
                <label className={labelClass}>{t('opleider.aboutProvider')}</label>
                <textarea value={form.aanbieder_bio} onChange={set('aanbieder_bio')} rows={2} className={`${inputClass} resize-none`} placeholder={t('opleider.aboutProviderPh')} />
              </div>
            </div>
          </Accordion>

          {/* SECTIE: Praktische details */}
          <Accordion title={t('opleider.sectionPractical')} open={openSection === 'praktisch'} onToggle={() => toggleSection('praktisch')}>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>{t('opleider.dateTime')}</label>
                <input type="text" value={form.datum_tijd} onChange={set('datum_tijd')} className={inputClass} placeholder={t('opleider.dateTimePh')} />
              </div>
              <div>
                <label className={labelClass}>{t('opleider.locationDetail')}</label>
                <input type="text" value={form.locatie_detail} onChange={set('locatie_detail')} className={inputClass} placeholder={t('opleider.locationDetailPh')} />
              </div>
              <div>
                <label className={labelClass}>{t('opleider.locationPostcode', 'Postcode leslocatie (4 cijfers)')}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={form.locatie_postcode ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, locatie_postcode: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                  className={inputClass}
                  placeholder={t('opleider.locationPostcodePh', 'bijv. 1012 — medewerkers filteren de Academy op reistijd; leeg = adres van uw organisatie')}
                />
              </div>
              <div>
                <label className={labelClass}>{t('opleider.studyLoad')}</label>
                <input type="text" value={form.studiebelasting} onChange={set('studiebelasting')} className={inputClass} placeholder={t('opleider.studyLoadPh')} />
              </div>
              <div>
                <label className={labelClass}>{t('opleider.studyMaterial')}</label>
                <input type="text" value={form.studiemateriaal_info} onChange={set('studiemateriaal_info')} className={inputClass} placeholder={t('opleider.studyMaterialPh')} />
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.inclusief_materialen} onChange={setCheck('inclusief_materialen')} className="w-4 h-4 rounded text-[#0B1F3A]" />
                  <span className="text-sm text-gray-700">{t('opleider.materialsIncluded')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.lunch_inbegrepen} onChange={setCheck('lunch_inbegrepen')} className="w-4 h-4 rounded text-[#0B1F3A]" />
                  <span className="text-sm text-gray-700">{t('opleider.lunchIncluded')}</span>
                </label>
              </div>
            </div>
          </Accordion>

          {/* SECTIE: Erkenning & Kwaliteit */}
          <Accordion title={t('opleider.sectionQuality')} open={openSection === 'erkenning'} onToggle={() => toggleSection('erkenning')}>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>{t('opleider.accreditation')}</label>
                <input type="text" value={form.accreditatie} onChange={set('accreditatie')} className={inputClass} placeholder={t('opleider.accreditationPh')} />
              </div>
              <div>
                <label className={labelClass}>{t('opleider.certification')}</label>
                <input type="text" value={form.certificering} onChange={set('certificering')} className={inputClass} placeholder={t('opleider.certificationPh')} />
              </div>
              <div>
                <label className={labelClass}>{t('opleider.instructors')}</label>
                <input type="text" value={form.docenten} onChange={set('docenten')} className={inputClass} placeholder={t('opleider.instructorsPh')} />
              </div>
              <div>
                <label className={labelClass}>{t('opleider.admissionReqs')}</label>
                <input type="text" value={form.toelatingseisen} onChange={set('toelatingseisen')} className={inputClass} placeholder={t('opleider.admissionReqsPh')} />
              </div>
            </div>
          </Accordion>

          {/* SECTIE: Media */}
          <Accordion title={t('opleider.sectionAttachments')} open={openSection === 'media'} onToggle={() => toggleSection('media')}>
            <div className="space-y-5">
              {/* Brochure */}
              <div>
                <label className={labelClass}>{t('opleider.brochure')}</label>
                <label className="cursor-pointer flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 w-fit">
                  <FileText className="w-4 h-4" />
                  {brochureFile ? brochureFile.name : t('opleider.uploadPdf')}
                  <input type="file" accept="application/pdf" onChange={(e) => setBrochureFile(e.target.files?.[0] || null)} className="hidden" />
                </label>
                <p className="text-xs text-gray-400 mt-1">Max. 10 MB</p>
              </div>

              {/* Video URL */}
              <div>
                <label className={labelClass}>{t('opleider.videoPitch')}</label>
                <input type="url" value={form.video_url} onChange={set('video_url')} className={inputClass} placeholder="https://www.youtube.com/watch?v=..." />
              </div>
            </div>
          </Accordion>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {(error as any)?.response?.data?.message || t('opleider.genericError')}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 bg-[#0B1F3A] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#1A3459] transition-colors disabled:opacity-60"
            >
              <Save className="w-4 h-4" />
              {isLoading ? t('opleider.saving') : isEdit ? t('opleider.saveChanges') : t('opleider.saveTraining')}
            </button>
            <Link to="/opleider/academy/trainingen" className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              {t('opleider.cancel')}
            </Link>
          </div>
        </form>

        {isEdit && form.academy_categorie && (
          <div className="mt-6">
            <OpleiderAcademySessies trainingId={parseInt(id!, 10)} />
          </div>
        )}
      </div>
    </div>
  );
};

const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] focus:border-transparent';

const Accordion: React.FC<{
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, open, onToggle, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
    >
      <span className="font-semibold text-gray-900">{title}</span>
      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && <div className="px-5 pb-5">{children}</div>}
  </div>
);

export default OpleiderTrainingFormPage;
